import { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import Cookies from 'js-cookie'

/**
 * Fetches the signed-in user's access token and stores it as the
 * `access_token` cookie the API client sends. Returns true once that has
 * settled: the cookie is set, or no one is signed in, or the fetch failed
 * (the next request then answers 401 and the client's refresh takes over).
 *
 * The app shows its loader until then (client/app.tsx). Before, the layout
 * fetched the token in its own effect, and a page's first read left ahead
 * of it — React runs a child's effects before its parent's — so the backend
 * refused the read with "No authentication token found" until a reload.
 */
export function useAccessTokenCookie(): boolean {
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0()
  const [ready, setReady] = useState(() => !!Cookies.get('access_token'))

  useEffect(() => {
    if (isLoading) return
    if (!isAuthenticated) {
      setReady(true)
      return
    }
    let cancelled = false
    Promise.resolve()
      .then(() => getAccessTokenSilently())
      .then(token => {
        if (token) Cookies.set('access_token', token, { expires: 1 })
      })
      .catch(error => console.error('Error getting access token:', error))
      .finally(() => {
        if (!cancelled) setReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [getAccessTokenSilently, isAuthenticated, isLoading])

  return ready
}
